import { CSSProperties, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useCookies } from 'react-cookie';
import { decode } from 'jsonwebtoken';
import { client } from '../../../apollo';
import { JWT_COOKIE_KEY, NETWORK_FAILED_TO_FETCH } from '../../../consts';
import { AUTHENTICATE_USER, FETCH_FLATFILE_KEY, LOGOUT_USER, SET_USER_ACTIVE_ORGANISATION } from '../../../graphql';
import { getAuthStateFromToken, getEmptyAuthState } from '../../../utils/auth';
import { clearIndexedDB, pwaOfflineQueryFetch, syncPwaOfflineData } from '../../../utils';
import {
  ONLINE_LOGIN_REQUIRED,
  PendingOfflineLogin,
  readOfflineLogin,
  saveOfflineLoginAfterClear,
  updateOfflineLoginToken,
  verifyOfflinePassword,
} from '../../../utils/offlineVerifier';
import { applyOrganisationSwitch } from './organisationSwitch';
import { AuthProvider, AuthProviderValue, AuthState, LoginFormValues } from './AuthContext';
import metabase from '../../../services/metabase';
import Loader from '../../../components/shared/Loader';
import { AuthenticateUserType, getUserData, writePortalOrganisation, writeUserData } from '../../../utils/idbFns';
import { useOnlineStatus } from '../../../hooks/useOnlineStatus';
import { shouldRecordLogoutOnServer } from '../../../utils/offlineSyncGate';
import { AUTHENTICATE_USERType } from '../../../graphql/mutations/auth/AuthenticateUser';
import { SET_USER_ACTIVE_ORGANISATIONType } from '../../../graphql/mutations/auth/SetUserActiveOrganisation';
import { GlobalAppContext } from '../../../components/shared/ContextProvider';

type Props = {
  children?: ReactNode;
};

const Auth: FC<Props> = ({ children }) => {
  // @ts-ignore
  const onlineStatus = useOnlineStatus();
  const [cookies, setCookie, removeCookie] = useCookies<any>([`${JWT_COOKIE_KEY}`]);
  const switchRouteRef = useRef<string | null>(null);
  const pendingOfflineLogin = useRef<PendingOfflineLogin | null>(null);
  const [loginResponse, setLoginResponse] = useState<AuthenticateUserType>(null);
  const [authState, setAuthState] = useState<AuthState>(getAuthStateFromToken());
  const [switchingOrganisation, setSwitchingOrganisation] = useState(false);
  const { setFlatFileLicenseKey } = useContext(GlobalAppContext);

  const [onlineCredentialsCheck] = useMutation(AUTHENTICATE_USER, {
    awaitRefetchQueries: false,
    // refetchQueries: [{ query: GET_AUTHENTICATED_USER }],
  });
  const [setUserActiveOrganisationMutation] = useMutation(SET_USER_ACTIVE_ORGANISATION, {
    awaitRefetchQueries: false,
  });
  const [logoutUser] = useMutation(LOGOUT_USER);

  const [fetch, { data }] = useLazyQuery(FETCH_FLATFILE_KEY);

  useEffect(() => {
    if (authState && authState.authenticated) {
      fetch({ variables: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    if (data && data.flatfilekey && data.flatfilekey.key) {
      setFlatFileLicenseKey(data.flatfilekey.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const offlineCredentialsCheck = async (email: string, password: string): Promise<AuthenticateUserType> => {
    const record = await readOfflineLogin(email);
    if (!record || !record.token) return undefined;
    const passwordMatches = await verifyOfflinePassword(password, record);
    if (!passwordMatches || !record.profile || !record.profile.token) return undefined;
    // Use the cached server token and profile as they are. Never create or extend a token.
    return record.profile;
  };

  useEffect(() => {
    if (!loginResponse) return;
    (async () => {
      const authenticateUser: AuthenticateUserType = loginResponse;
      const accessToken = authenticateUser.token;
      const newAuthState = getAuthStateFromToken(accessToken);

      // Clear Indexed DB only when a different user signs in and accepts the erase prompt.
      // Save the new verifier after that clear, or clear() deletes it too.
      const pending = pendingOfflineLogin.current;
      pendingOfflineLogin.current = null;
      await saveOfflineLoginAfterClear(pending, () => clearIndexedDB(newAuthState.user.id || ''));
      setCookie(`${JWT_COOKIE_KEY}`, accessToken, {
        path: '/',
        maxAge: 28800, // Will expire after 8hr (value is in number of sec.)
      });
      setCookie(`${JWT_COOKIE_KEY}_SET`, '1', {
        path: '/',
        maxAge: 2592000,
      });

      console.log('Auth.useEffect.writeUserData', authenticateUser);
      await writeUserData(authenticateUser, authenticateUser.user.id);

      // fetch queries to store in Indexed DB for offline access
      if (onlineStatus.isOnline && navigator.onLine) {
        syncPwaOfflineData();
        await writePortalOrganisation(null, 'Auth.useEffect');
        pwaOfflineQueryFetch(authenticateUser.user.id);
      }

      setAuthState(Object.assign({}, authState, newAuthState));
      setSwitchingOrganisation(false);
      if (switchRouteRef.current) {
        window.location.replace(switchRouteRef.current);
        switchRouteRef.current = null;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginResponse]);

  const {
    authenticated,
    acknowledgedDeclaration,
    user,
    accessToken,
    defaultRedirectRoute,
    error,
    loading,
    organisationId,
    sessionId,
    hasCaseManagement,
  } = authState;

  const login: (values: LoginFormValues) => Promise<void> = async values => {
    pendingOfflineLogin.current = null;
    removeCookie(`${JWT_COOKIE_KEY}`);
    removeCookie(`${JWT_COOKIE_KEY}_SET`);
    metabase.api.disconnect();
    setAuthState(getAuthStateFromToken());

    var authenticateUser: AuthenticateUserType = null;
    try {
      const onlineCredentialsCheckResult = await onlineCredentialsCheck({
        variables: {
          email: values.email,
          password: values.password,
        },
      });
      const data: AUTHENTICATE_USERType = onlineCredentialsCheckResult.data;

      if (data && data.authenticateUser) {
        authenticateUser = data.authenticateUser;
        pendingOfflineLogin.current = {
          email: values.email,
          password: values.password,
          token: authenticateUser.token,
          profile: authenticateUser,
        };
        await writeUserData(authenticateUser, authenticateUser.user.id);
        setLoginResponse(authenticateUser);
        return;
      }
    } catch (e) {
      const error = e as any;
      if (!error || !error.message || error.message !== NETWORK_FAILED_TO_FETCH) {
        throw error;
      }
    }

    // reach here, we're offline.
    authenticateUser = await offlineCredentialsCheck(values.email, values.password);
    if (!!authenticateUser) {
      await writeUserData(authenticateUser, authenticateUser.user.id);
      //await writeUserOrganisations(authenticateUser.user.organisations, authenticateUser.user.id);
      setLoginResponse(authenticateUser);
    } else {
      setLoginResponse(null);
      // eslint-disable-next-line no-throw-literal
      throw {
        message: ONLINE_LOGIN_REQUIRED,
      };
    }
  };

  const clearSessionCookies = () => {
    const token = cookies[`${JWT_COOKIE_KEY}`];
    const decodedJWT: any = decode(token);
    const tokenExpValid = decodedJWT && decodedJWT.exp ? Date.now() / 1000 < decodedJWT.exp : false;

    // Adding this because sometimes remove cookie from react cookie does not work, especially when one has had a long session
    removeCookie(`${JWT_COOKIE_KEY}`, { path: '/' });
    document.cookie = `name=${JWT_COOKIE_KEY}; expires=Sat, 20 Jan 1980 12:00:00 UTC; path=/`;

    if (token && tokenExpValid) {
      removeCookie(`${JWT_COOKIE_KEY}_SET`);
      document.cookie = `name=${JWT_COOKIE_KEY}_SET; expires=Sat, 20 Jan 1980 12:00:00 UTC; path=/`;
    }
  };

  const logout: () => void = async () => {
    // Leave the offline verifier in IndexedDB. A field user can sign in again without a network.
    metabase.api.disconnect();

    setAuthState(getEmptyAuthState());

    const browserOnline = typeof navigator === 'undefined' || navigator.onLine;
    if (!shouldRecordLogoutOnServer(onlineStatus.isOnline && browserOnline)) {
      client.clearStore().catch(() => undefined);
      clearSessionCookies();
      return;
    }

    // Update the auth metadata table when the server can be reached.
    logoutUser()
      .then(() => {
        client.clearStore();
      })
      .catch((reason: any) => {
        if (reason && reason.message !== NETWORK_FAILED_TO_FETCH) {
          client.clearStore();
        }
      })
      .finally(() => {
        clearSessionCookies();
      });
  };

  const setAcknowledgeDeclaration: (acknowledgeDeclaration: boolean) => void = acknowledgedDeclaration => {
    const authState = getAuthStateFromToken();
    authState.acknowledgedDeclaration = acknowledgedDeclaration;
    setAuthState(authState);
  };

  const setActiveOrganisation: (route: string, organisationId: string, onDone?: () => void) => void = async (
    route,
    organisationId,
    onDone
  ) => {
    const authState = getAuthStateFromToken();
    if (!authState.user.id) return;
    const switchToAdminPortal = route.startsWith('/admin');
    if (
      (switchToAdminPortal && authState.organisationId === '') ||
      (!switchToAdminPortal && authState.organisationId === organisationId)
    )
      return;

    setSwitchingOrganisation(true);
    switchRouteRef.current = route;
    try {
      metabase.api.disconnect();
      const setUserActiveOrganisationMutationResult = await setUserActiveOrganisationMutation({
        variables: {
          input: {
            organisationId,
          },
        },
      });
      const data = setUserActiveOrganisationMutationResult.data as SET_USER_ACTIVE_ORGANISATIONType;
      const serverToken = data && data.authenticateUser && data.authenticateUser.token;
      const switchedToken = await applyOrganisationSwitch(
        {
          readProfile: getUserData,
          writeProfile: writeUserData,
          writeCookie: token =>
            setCookie(`${JWT_COOKIE_KEY}`, token, {
              path: '/',
              maxAge: 28800, // Will expire after 8hr (value is in number of sec.)
            }),
        },
        authState.user.id,
        serverToken
      );
      await client.clearStore();
      const authenticateUser = await getUserData(authState.user.id);
      try {
        await updateOfflineLoginToken({
          email: authState.user.email || '',
          token: switchedToken,
          profile: authenticateUser || undefined,
        });
      } catch (verifierError) {
        console.error('Could not update the offline login token', verifierError);
      }
      if (authenticateUser) {
        setLoginResponse(authenticateUser);
      } else {
        setAuthState(getAuthStateFromToken(switchedToken));
      }
      if (onDone) onDone();
      if (switchRouteRef.current) {
        const nextRoute = switchRouteRef.current;
        switchRouteRef.current = null;
        window.location.replace(nextRoute);
      }
    } catch (error) {
      switchRouteRef.current = null;
      const message = (error && (error as any).message) || 'Unable to switch organisation.';
      window.alert(message);
    } finally {
      setSwitchingOrganisation(false);
    }
  };

  const authProviderValue: AuthProviderValue = {
    authenticated,
    acknowledgedDeclaration,
    hasCaseManagement,
    defaultRedirectRoute,
    user,
    accessToken,
    error,
    loading,
    organisationId,
    sessionId,
    password: '',
    login,
    logout,
    setActiveOrganisation,
    setAcknowledgeDeclaration,
    switchingOrganisation,
  };

  const switchOverlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <AuthProvider value={authProviderValue}>
      {switchingOrganisation && (
        <div style={switchOverlayStyle}>
          <Loader loading={true} color="white" width={50} height={50} />
        </div>
      )}
      {children}
    </AuthProvider>
  );
};

export default Auth;
